import React from 'react';
import { i18n } from 'config/locales';
import ValidationElement from 'components/Form/ValidationElement';
import Field from 'components/Form/Field';
import Telephone from 'components/Form/Telephone';
import Location from 'components/Form/Location';
import PVQLocation from 'components/Form/PVQLocation';
import Text from 'components/Form/Text';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import { PsychologicalTreatmentTypeModel } from 'forms/sections/psychological/shared/TreatmentType';
import {
  CommonAddressBooks,
  CommonOnError,
  CommonOnUpdate,
  CommonScrollIntoView,
} from 'types/props';
import { Dispatch } from 'redux';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import { AccordionItemProps } from 'components/Form/Accordion/types';
import Comments from 'components/Form/Comments';
import { isPVQForm } from 'helpers/form';
import NotApplicable from 'components/Form/NotApplicable';
import PVQName from 'components/Form/PVQName';

interface Props
  extends ValidationElementProps,
  Partial<PsychologicalTreatmentTypeModel>,
  Partial<AccordionItemProps> {
  scrollIntoView?: CommonScrollIntoView
  dispatch?: Dispatch
  required?: boolean
  prefix?: string
  addressBooks?: CommonAddressBooks
  onError?: CommonOnError
  onUpdate?: CommonOnUpdate
  attribute?: string
  includeComment?: boolean
  formType?: string
  showFullName?: boolean
}

export default class Treatment extends ValidationElement<Props> {
  update = (queue: Partial<PsychologicalTreatmentTypeModel>) => {
    this.props.onUpdate({
      Name: this.props.Name,
      HealthCareProviderFullName: this.props.HealthCareProviderFullName,
      HealthCareProviderFullNameNotApplicable: this.props.HealthCareProviderFullNameNotApplicable,
      Phone: this.props.Phone,
      Address: this.props.Address,
      Comments: this.props.Comments,
      ...queue,
    });
  };

  updateName = (values: PsychologicalTreatmentTypeModel['Name']) => {
    this.update({
      Name: values,
    });
  };

  updateHealthCareProviderFullName = (values: PsychologicalTreatmentTypeModel['HealthCareProviderFullName']) => {
    this.update({
      HealthCareProviderFullName: values,
    });
  };

  updateHealthCareProviderFullNameNotApplicable = (values: PsychologicalTreatmentTypeModel['HealthCareProviderFullNameNotApplicable']) => {
    this.update({
      HealthCareProviderFullName: values?.applicable ? this.props.HealthCareProviderFullName : {},
      HealthCareProviderFullNameNotApplicable: values,
    });
  };

  updatePhone = (values: PsychologicalTreatmentTypeModel['Phone']) => {
    this.update({
      Phone: values,
    });
  };

  updateAddress = (values: PsychologicalTreatmentTypeModel['Address']) => {
    this.update({
      Address: values,
    });
  };

  updateComments = (values: PsychologicalTreatmentTypeModel['Comments']) => {
    this.update({
      Comments: values,
    });
  };

  render() {
    const prefix = this.props.prefix;

    // Treatment component is used as both an AccordionItem and as a
    // non-AccordionItem. This allows support of both.
    let attribute = this.props.attribute;
    if (this.props.bind) { attribute = `${this.props.attribute}.items[${this.props.$index}].Item`; }

    const isPvq = isPVQForm(this.props.formType);
    return isPvq ? (
      <div className="treatment">
        {this.props.showFullName ? (
          <Field
            title={i18n.t(`psychological.pvq.${prefix}.heading.name`)}
            attribute={`${attribute}.HealthCareProviderFullName`}
            titleSize="label"
            adjustFor="labels"
            className="no-margin-bottom"
            scrollIntoView={this.props.scrollIntoView}
          >
            <NotApplicable
              {...this.props.HealthCareProviderFullNameNotApplicable}
              name="HealthCareProviderFullNameNotApplicable"
              className="healthcare-provider-fullname"
              onUpdate={this.updateHealthCareProviderFullNameNotApplicable}
              onError={this.props.onError}
              or="or"
              label="I Dont Know"
              required={this.props.required}
              attribute={`${attribute}.HealthCareProviderFullNameNotApplicable`}
            >
              <PVQName
                {...this.props.HealthCareProviderFullName}
                name="healthcare-provider-fullname"
                attribute={`${attribute}.HealthCareProviderFullName`}
                onUpdate={this.updateHealthCareProviderFullName}
                onError={this.props.onError}
                className="healthcare-provider-fullname"
                required={this.props.required}
                scrollIntoView={this.props.scrollIntoView}
                hideMiddleName
                showLastFirstMiddleNameOrder
              />
            </NotApplicable>
          </Field>
        ) : (
          <Field
            title={i18n.t(`psychological.pvq.${prefix}.heading.name`)}
            attribute={`${attribute}.Name`}
            titleSize="label"
            adjustFor="labels"
            className="no-margin-bottom"
            scrollIntoView={this.props.scrollIntoView}
          >
            <Text
              name="Name"
              attribute={`${attribute}.Name`}
              label={i18n.t(`psychological.pvq.${prefix}.label.name`)}
              className="treatment-name"
              {...this.props.Name}
              onUpdate={this.updateName}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
        )}

        <Field
          className="override-required treatment-telephone"
          attribute={`${attribute}.Phone`}
          adjustFor="telephone"
          scrollIntoView={this.props.scrollIntoView}
        >
          <Telephone
            name="Phone"
            attribute={`${attribute}.Phone`}
            label={i18n.t(`psychological.pvq.${prefix}.label.phone`)}
            {...this.props.Phone}
            allowNotApplicable={false}
            onUpdate={this.updatePhone}
            onError={this.props.onError}
            required={this.props.required}
            showNumberType
            showTimeOfDay
            timeOfDayLabel={i18n.t('identification.pvq.contacts.collection.phoneNumbers.timeOfDayLabel')}
          />
        </Field>

        <Field
          attribute={`${attribute}.Address`}
          adjustFor="address"
          scrollIntoView={this.props.scrollIntoView}
          shrink
          dataTestId="Address"
        >
          <PVQLocation
            name="Address"
            attribute={`${attribute}.Address`}
            {...this.props.Address}
            label={i18n.t(`psychological.pvq.${prefix}.label.address`)}
            help={`psychological.pvq.${prefix}.help.address`}
            domesticTitle={i18n.t(`psychological.pvq.${prefix}.label.addressDomesticTitle`)}
            foreignTitle={i18n.t(`psychological.pvq.${prefix}.label.addressForeignTitle`)}
            layout={Location.ADDRESS}
            geocode
            addressBook="Facility"
            addressBooks={this.props.addressBooks}
            dispatch={this.props.dispatch}
            onUpdate={this.updateAddress}
            onError={this.props.onError}
            required={this.props.required}
            isPoBoxAllowed={false}
            showInternationalStreet
          />
        </Field>
      </div>
    ) : (
      <div className="treatment">
        <Field
          title={i18n.t(`psychological.${prefix}.heading.name`)}
          attribute={`${attribute}.Name`}
          titleSize="label"
          adjustFor="labels"
          className="no-margin-bottom"
          scrollIntoView={this.props.scrollIntoView}
        >
          <Text
            name="Name"
            attribute={`${attribute}.Name`}
            label={i18n.t(`psychological.${prefix}.label.name`)}
            className="treatment-name"
            {...this.props.Name}
            onUpdate={this.updateName}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field
          className="override-required treatment-telephone"
          attribute={`${attribute}.Phone`}
          adjustFor="telephone"
          scrollIntoView={this.props.scrollIntoView}
        >
          <Telephone
            name="Phone"
            attribute={`${attribute}.Phone`}
            label={i18n.t(`psychological.${prefix}.label.phone`)}
            {...this.props.Phone}
            allowNotApplicable={false}
            onUpdate={this.updatePhone}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field
          title={i18n.t(`psychological.${prefix}.heading.address`)}
          attribute={`${attribute}.Address`}
          help={`psychological.${prefix}.help.address`}
          adjustFor="address"
          scrollIntoView={this.props.scrollIntoView}
        >
          <Location
            name="Address"
            attribute={`${attribute}.Address`}
            {...this.props.Address}
            label={i18n.t(`psychological.${prefix}.label.address`)}
            layout={Location.ADDRESS}
            geocode
            addressBooks={this.props.addressBooks}
            addressBook="Facility"
            showPostOffice
            dispatch={this.props.dispatch}
            onUpdate={this.updateAddress}
            onError={this.props.onError}
            required={this.props.required}
            isPoBoxAllowed={false}
          />
        </Field>
        {this.props.includeComment && (
          <Comments
            title={i18n.t(`psychological.${prefix}.comments`)}
            name="Comments"
            className="optional-comments"
            attribute={`${attribute}.Comments`}
            {...this.props.Comments}
            onUpdate={this.updateComments}
            required={false}
          />
        )}
      </div>
    );
  }
}

Treatment.defaultProps = {
  prefix: 'treatment',
  addressBooks: {},
  Comments: {},
  includeComment: false,
  formType: '',
  showFullName: false,
  HealthCareProviderFullName: {},
  HealthCareProviderFullNameNotApplicable: { applicable: true },
  dispatch: (action) => { },
  onUpdate: (queue) => { },
  onError: (value, arr: ErrorCode[]) => arr,
};
